import { useEffect, useState } from 'react';
import { Agents as AgentsApi, Alerts as AlertsApi } from '../lib/api';
import { Pill, Spinner, EmptyState } from '../components/Common';
import clsx from '../lib/clsx';
import type { AlertHistory, AlertRule, AgentSummary } from '@freebuff/shared';

const METRICS: Array<{ id: AlertRule['metric']; label: string; hint?: string }> = [
  { id: 'cpu_pct', label: 'CPU (%)', hint: 'Média móvel na janela' },
  { id: 'mem_pct', label: 'Memória (%)', hint: 'Média móvel na janela' },
  { id: 'disk_pct', label: 'Disco (%)', hint: 'Pior partição na janela' },
  { id: 'load1', label: 'Load average (1 min)' },
  { id: 'http_probe_ok', label: 'HTTP probes ok (%)', hint: '0.x = abaixo desse % é alerta' },
  { id: 'agent_offline', label: 'Agente offline' },
  { id: 'service_active', label: 'Serviço inativo' },
];

export function Alerts() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [history, setHistory] = useState<AlertHistory[]>([]);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const refresh = async () => {
    const [rulesR, histR, agentsR] = await Promise.all([
      AlertsApi.rules(),
      AlertsApi.history({ includeResolved: showResolved, limit: 200 }),
      AgentsApi.list(),
    ]);
    setRules(rulesR.rules);
    setHistory(histR.history);
    setAgents(agentsR.agents);
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [showResolved]);

  const create = async (data: Partial<AlertRule> & Pick<AlertRule, 'name' | 'metric' | 'operator' | 'threshold'>) => {
    await AlertsApi.createRule(data);
    setShowAdd(false);
    await refresh();
  };
  const toggle = async (r: AlertRule) => {
    await AlertsApi.updateRule(r.id, { enabled: !r.enabled });
    await refresh();
  };
  const remove = async (r: AlertRule) => {
    if (!confirm(`Remover regra "${r.name}"?`)) return;
    await AlertsApi.deleteRule(r.id);
    await refresh();
  };
  const resolve = async (h: AlertHistory) => {
    await AlertsApi.resolveHistory(h.id);
    await refresh();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="card-header">
          <div>
            <h2 className="text-lg font-semibold">Regras</h2>
            <p className="text-xs text-slate-400">Avaliadas a cada minuto com janela e cooldown configuráveis.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Nova regra</button>
        </div>
        <div className="card-body">
          {rules.length === 0 ? <EmptyState title="Nenhuma regra" hint="Crie sua primeira regra de alerta." /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Nome</th><th>Métrica</th><th>Condição</th><th>Janela</th><th>Cooldown</th><th>Estado</th><th /></tr></thead>
                <tbody>
                  {rules.map((r: AlertRule) => (
                    <tr key={r.id}>
                      <td className="font-semibold text-white">{r.name}</td>
                      <td className="font-mono text-xs text-slate-400">{r.metric}</td>
                      <td className="font-mono text-sm">{r.operator} {r.threshold}</td>
                      <td className="text-xs text-slate-400">{r.windowSec}s</td>
                      <td className="text-xs text-slate-400">{Math.round(r.cooldownSec / 60)}m</td>
                      <td>
                        <button onClick={() => toggle(r)} className={clsx('pill', r.enabled ? 'pill-ok' : 'pill-muted')}>
                          {r.enabled ? 'Ativa' : 'Pausada'}
                        </button>
                      </td>
                      <td className="text-right">
                        <button onClick={() => remove(r)} className="btn text-danger hover:text-red-400">Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <div>
            <h2 className="text-lg font-semibold">Histórico de alertas</h2>
            <p className="text-xs text-slate-400">Disparos recentes e situações resolvidas.</p>
          </div>
          <button onClick={() => setShowResolved((v) => !v)} className="btn">{showResolved ? 'Ocultar resolvidos' : 'Mostrar resolvidos'}</button>
        </div>
        <div className="card-body">
          {history.length === 0 ? <EmptyState title="Nenhum alerta disparado ainda" /> : (
            <div className="overflow-x-auto -mx-5">
              <table className="table">
                <thead><tr><th>Quando</th><th>Agente</th><th>Regra</th><th>Mensagem</th><th>Estado</th><th /></tr></thead>
                <tbody>
                  {history.map((h: AlertHistory) => {
                    const rule = rules.find((r) => r.id === h.ruleId);
                    const agent = agents.find((a) => a.id === h.agentId);
                    return (
                      <tr key={h.id}>
                        <td className="text-xs text-slate-400">{new Date(h.ts).toLocaleString('pt-BR')}</td>
                        <td className="text-sm">{agent?.name ?? h.agentId?.slice(0, 6) ?? '—'}</td>
                        <td className="text-sm">{rule?.name ?? `#${h.ruleId}`}</td>
                        <td className="text-sm text-slate-300 max-w-md truncate">{h.message}</td>
                        <td>{h.resolved ? <Pill tone="ok">Resolvido</Pill> : <Pill tone="error">Aberto</Pill>}</td>
                        <td className="text-right">
                          {!h.resolved && <button onClick={() => resolve(h)} className="btn">Marcar resolvido</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showAdd && (
        <Modal title="Nova regra de alerta" onClose={() => setShowAdd(false)}>
          <NewRuleForm agents={agents} onCancel={() => setShowAdd(false)} onSubmit={create} />
        </Modal>
      )}
    </div>
  );
}

function NewRuleForm({ agents, onSubmit, onCancel }: {
  agents: AgentSummary[];
  onSubmit: (d: Partial<AlertRule> & Pick<AlertRule, 'name' | 'metric' | 'operator' | 'threshold'>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [metric, setMetric] = useState<AlertRule['metric']>('cpu_pct');
  const [operator, setOperator] = useState<AlertRule['operator']>('>');
  const [threshold, setThreshold] = useState(90);
  const [windowSec, setWindowSec] = useState(60);
  const [cooldownSec, setCooldownSec] = useState(300);
  const [agentId, setAgentId] = useState<string>(''); // '' = todos
  const [channel, setChannel] = useState<AlertRule['channel']>('webhook');
  const [webhookUrlOverride, setWebhookUrlOverride] = useState<string>('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name, metric, operator, threshold,
          windowSec, cooldownSec,
          agentId: agentId || null,
          channel,
          webhookUrlOverride: webhookUrlOverride || null,
          enabled: true,
        });
      }}
      className="space-y-4"
    >
      <div>
        <label className="label">Nome da regra</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required className="input" placeholder="CPU alta em produção" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Métrica</label>
          <select value={metric} onChange={(e) => setMetric(e.target.value as AlertRule['metric'])} className="input">
            {METRICS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Agente (vazio = todos)</label>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="input">
            <option value="">Todos os agentes</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">Operador</label>
          <select value={operator} onChange={(e) => setOperator(e.target.value as AlertRule['operator'])} className="input">
            <option value=">">&gt;</option>
            <option value="<">&lt;</option>
            <option value="==">=</option>
            <option value="!=">≠</option>
          </select>
        </div>
        <div>
          <label className="label">Threshold</label>
          <input type="number" step="any" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} className="input" />
        </div>
        <div>
          <label className="label">Canal</label>
          <select value={channel} onChange={(e) => setChannel(e.target.value as AlertRule['channel'])} className="input">
            <option value="webhook">Webhook</option>
            <option value="log">Apenas log</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Janela (segundos)</label>
          <input type="number" min={10} max={3600} value={windowSec} onChange={(e) => setWindowSec(Number(e.target.value))} className="input" />
        </div>
        <div>
          <label className="label">Cooldown (segundos)</label>
          <input type="number" min={30} max={86400} value={cooldownSec} onChange={(e) => setCooldownSec(Number(e.target.value))} className="input" />
        </div>
      </div>
      {channel === 'webhook' && (
        <div>
          <label className="label">Webhook URL override (opcional, deixa vazio para usar o padrão)</label>
          <input value={webhookUrlOverride} onChange={(e) => setWebhookUrlOverride(e.target.value)} type="url" className="input font-mono text-sm" placeholder="https://discord.com/api/webhooks/..." />
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn">Cancelar</button>
        <button type="submit" disabled={!name} className="btn btn-primary">Criar regra</button>
      </div>
    </form>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-30 p-4 animate-fadeIn" onClick={onClose}>
      <div className={clsx('card w-full max-w-xl')} onClick={(e) => e.stopPropagation()}>
        <div className="card-header"><h3 className="font-semibold">{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-white">×</button></div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  );
}
